package br.com.htms.inspeccaocampo.platform

import androidx.compose.runtime.NoLiveLiterals
import kotlin.coroutines.resume
import kotlinx.coroutines.suspendCancellableCoroutine
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import org.khronos.webgl.get
import org.khronos.webgl.set
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag

/**
 * Cliente do Worker R2 (upload + download).
 *
 * Config via `window.__ICD_R2__ = { baseUrl }` em index.html.
 * O token de upload é obtido em runtime via GET /upload-token (exige login).
 * NÃO embutir UPLOAD_TOKEN / secret no HTML.
 * Fotos no R2 expiram em **60 dias**.
 */
@NoLiveLiterals
internal object R2Photos {
    data class Config(val baseUrl: String)

    private var cachedToken: String? = null
    private var cachedTokenExpMs: Double = 0.0

    private const val AUTH_TOKEN_KEY = "icd_auth_token"

    /** Bearer da sessão (mesmo storage do AuthApi). */
    private fun sessionBearer(): String? {
        return try {
            val t = kotlinx.browser.localStorage.getItem(AUTH_TOKEN_KEY)?.trim()
            if (t.isNullOrBlank()) null else t
        } catch (_: Throwable) {
            null
        }
    }

    fun configOrNull(): Config? {
        return try {
            val raw = js("typeof window !== 'undefined' ? window.__ICD_R2__ : null")
            if (raw == null || raw == undefined) return null
            val base = (raw.baseUrl as? String)?.trim()?.trimEnd('/') ?: return null
            if (base.isBlank() || !base.startsWith("http")) return null
            Config(baseUrl = base)
        } catch (_: Throwable) {
            null
        }
    }

    fun isRemotePath(path: String): Boolean =
        path.startsWith("http://") ||
            path.startsWith("https://") ||
            path.startsWith("r2:")

    /** Envia JPEG; retorna URL pública GET do Worker, ou null se R2 não configurado / falha. */
    suspend fun upload(bytes: ByteArray, contentType: String = "image/jpeg"): String? {
        if (bytes.isEmpty()) return null
        if (!PlatformServices.isNetworkAvailable()) {
            consoleWarn("R2 upload: offline — pulando")
            return null
        }
        val cfg = configOrNull() ?: return null
        val token = fetchUploadToken(cfg) ?: run {
            consoleWarn("R2: sem token de upload (GET /upload-token falhou)")
            return null
        }
        return suspendCancellableCoroutine { cont ->
            try {
                val u8 = Uint8Array(bytes.size)
                bytes.forEachIndexed { i, b -> u8[i] = b }
                val blob = Blob(arrayOf(u8), BlobPropertyBag(type = contentType))
                val opts = js("({})")
                opts.method = "POST"
                opts.headers = js("({})")
                opts.headers["Content-Type"] = contentType
                opts.headers["X-Upload-Token"] = token
                sessionBearer()?.let { opts.headers["Authorization"] = "Bearer $it" }
                opts.body = blob
                val promise: dynamic = js("fetch")("${cfg.baseUrl}/upload", opts)
                promise.then(
                    { response ->
                        val ok = (response.ok as? Boolean) == true
                        if (!ok) {
                            consoleWarn("R2 upload HTTP ${response.status}")
                            cachedToken = null
                            if (cont.isActive) cont.resume(null)
                        } else {
                            response.text().then(
                                { text ->
                                    val parsedUrl = parseUploadUrl(text as String, cfg.baseUrl)
                                    if (cont.isActive) cont.resume(parsedUrl)
                                },
                                {
                                    if (cont.isActive) cont.resume(null)
                                }
                            )
                        }
                        null
                    },
                    { err ->
                        consoleWarn("R2 upload rede: $err")
                        if (cont.isActive) cont.resume(null)
                        null
                    }
                )
            } catch (t: Throwable) {
                consoleWarn("R2 upload: ${t.message}")
                if (cont.isActive) cont.resume(null)
            }
        }
    }

    suspend fun fetchBytes(path: String): ByteArray? {
        val fetchUrl = resolveFetchUrl(path) ?: return null
        val bearer = sessionBearer()
        return suspendCancellableCoroutine { cont ->
            try {
                val opts = js("({})")
                opts.method = "GET"
                if (!bearer.isNullOrBlank()) {
                    opts.headers = js("({})")
                    opts.headers["Authorization"] = "Bearer $bearer"
                }
                val promise: dynamic = js("fetch")(fetchUrl, opts)
                promise.then(
                    { response ->
                        val ok = (response.ok as? Boolean) == true
                        if (!ok) {
                            consoleWarn("R2 fetch HTTP ${response.status} $fetchUrl")
                            if (cont.isActive) cont.resume(null)
                        } else {
                            response.arrayBuffer().then(
                                { buf ->
                                    val u = Uint8Array(buf as ArrayBuffer)
                                    val out = ByteArray(u.length) { i -> u[i] }
                                    if (cont.isActive) cont.resume(out)
                                },
                                {
                                    consoleWarn("R2 fetch arrayBuffer falhou")
                                    if (cont.isActive) cont.resume(null)
                                }
                            )
                        }
                        null
                    },
                    { err ->
                        consoleWarn("R2 fetch rede: $err")
                        if (cont.isActive) cont.resume(null)
                        null
                    }
                )
            } catch (t: Throwable) {
                consoleWarn("R2 fetch: ${t.message}")
                if (cont.isActive) cont.resume(null)
            }
        }
    }

    private suspend fun fetchUploadToken(cfg: Config): String? {
        val bearer = sessionBearer()
        if (bearer.isNullOrBlank()) {
            consoleWarn("R2: sem sessão — login necessário para /upload-token")
            return null
        }
        val now = js("Date.now()") as Double
        val cached = cachedToken
        if (cached != null && now < cachedTokenExpMs - 30_000) {
            return cached
        }
        return suspendCancellableCoroutine { cont ->
            try {
                val opts = js("({})")
                opts.method = "GET"
                opts.headers = js("({})")
                opts.headers["Authorization"] = "Bearer $bearer"
                val promise: dynamic = js("fetch")("${cfg.baseUrl}/upload-token", opts)
                promise.then(
                    { response ->
                        val ok = (response.ok as? Boolean) == true
                        if (!ok) {
                            consoleWarn("R2 /upload-token HTTP ${response.status}")
                            if (cont.isActive) cont.resume(null)
                        } else {
                            response.text().then(
                                { text ->
                                    try {
                                        val parsed = js("JSON.parse")(text)
                                        val token = parsed.token as? String
                                        val exp = (parsed.expiresAt as? Number)?.toDouble()
                                        if (!token.isNullOrBlank() && exp != null) {
                                            cachedToken = token
                                            cachedTokenExpMs = exp
                                            if (cont.isActive) cont.resume(token)
                                        } else {
                                            if (cont.isActive) cont.resume(null)
                                        }
                                    } catch (t: Throwable) {
                                        consoleWarn("R2 /upload-token parse: ${t.message}")
                                        if (cont.isActive) cont.resume(null)
                                    }
                                },
                                { if (cont.isActive) cont.resume(null) }
                            )
                        }
                        null
                    },
                    { err ->
                        consoleWarn("R2 /upload-token rede: $err")
                        if (cont.isActive) cont.resume(null)
                        null
                    }
                )
            } catch (t: Throwable) {
                consoleWarn("R2 /upload-token: ${t.message}")
                if (cont.isActive) cont.resume(null)
            }
        }
    }

    private fun resolveFetchUrl(path: String): String? {
        when {
            path.startsWith("http://") || path.startsWith("https://") -> return path
            path.startsWith("r2:") -> {
                val key = path.removePrefix("r2:")
                val base = configOrNull()?.baseUrl ?: return null
                return "$base/foto/${encodeURIComponent(key)}"
            }
            else -> return null
        }
    }

    private fun parseUploadUrl(text: String, baseUrl: String): String? {
        return try {
            val parsed = js("JSON.parse")(text)
            val url = parsed.url as? String
            if (!url.isNullOrBlank()) return url
            val key = parsed.key as? String ?: return null
            "$baseUrl/foto/${encodeURIComponent(key)}"
        } catch (t: Throwable) {
            consoleWarn("R2 parseUploadUrl: ${t.message}")
            null
        }
    }

    private fun encodeURIComponent(s: String): String =
        js("encodeURIComponent")(s) as String

    private fun consoleWarn(msg: String) {
        try {
            js("console").warn("[ICD R2] $msg")
        } catch (_: Throwable) {
        }
    }
}
